import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle, Circle, Volume2, Mic, Square, Loader2 } from 'lucide-react';
import { fetchAudio, assessPronunciation } from '../../services/api';
import { useWavRecorder } from '../../hooks/useWavRecorder';
import ExerciseCard from '../shared/ExerciseCard';
import TranslatableText from '../shared/TranslatableText';
import PronunciationWords, { scoreColor } from '../shared/PronunciationWords';

// Cap recording length — keeps clips short and Azure costs predictable.
const MAX_RECORDING_SECONDS = 10;

const SpeakingPanel = ({
  questions,
  userAnswers,
  onSubmit,
  allAnswered,
  topic,
  pronunciationResults = {},
  onPronunciationResult,
}) => {
  const [playingId, setPlayingId] = useState(null);
  const [recordingId, setRecordingId] = useState(null);
  const [assessingId, setAssessingId] = useState(null);
  const [assessError, setAssessError] = useState(null);

  const { start, stop } = useWavRecorder();

  // Silent auto-stop timer — enforces the limit without showing a countdown.
  const autoStopRef = useRef(null);

  const clearRecordTimers = () => {
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
  };

  // Clear the timer if the component unmounts mid-recording.
  useEffect(() => {
    return () => {
      if (autoStopRef.current) clearTimeout(autoStopRef.current);
    };
  }, []);

  const playAudio = async (questionId, hintText) => {
    try {
      setPlayingId(questionId);

      // Use the new authenticated audio fetch function
      const audioBlob = await fetchAudio(hintText);

      const blobUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(blobUrl);

      audio.onended = () => {
        setPlayingId(null);
        URL.revokeObjectURL(blobUrl);
      };

      audio.play().catch((err) => {
        console.error('Error playing audio:', err);
        setPlayingId(null);
        URL.revokeObjectURL(blobUrl);
      });
    } catch (error) {
      console.error('Error:', error);
      setPlayingId(null);
    }
  };

  // Stop recording and send to Azure. Used both by the manual button and the
  // auto-stop timer, so the 10 s limit and a click behave identically.
  const stopAndAssess = async (question) => {
    clearRecordTimers();
    setRecordingId(null);
    setAssessingId(question.id);
    try {
      const blob = await stop();
      if (!blob) {
        setAssessError('Nahrávka je prázdná, zkuste to znovu.');
        return;
      }
      const result = await assessPronunciation(blob, question.hint);
      onPronunciationResult(question.id, result);
    } catch (err) {
      console.error('Pronunciation assessment failed:', err);
      setAssessError(err.message || 'Vyhodnocení výslovnosti selhalo.');
    } finally {
      setAssessingId(null);
    }
  };

  // Toggle recording: first click starts, second click stops and assesses.
  const handleRecordClick = async (question) => {
    setAssessError(null);

    if (recordingId === question.id) {
      await stopAndAssess(question);
      return;
    }

    // Don't start a second recording while another is running.
    if (recordingId !== null) return;
    try {
      await start();
      setRecordingId(question.id);

      // Force-stop at the limit — no visible countdown.
      autoStopRef.current = setTimeout(
        () => stopAndAssess(question),
        MAX_RECORDING_SECONDS * 1000
      );
    } catch {
      setAssessError('Nepodařilo se spustit nahrávání. Povolte přístup k mikrofonu.');
    }
  };

  return (
    <ExerciseCard>
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-xl font-semibold text-gray-800">
          Přečtěte větu nahlas a nahrajte ji
        </h2>
        <div className="text-sm font-medium text-gray-500">
          {Object.keys(userAnswers).length} / {questions.length}
        </div>
      </div>

      {/* Tell the user up front that recording is time-limited. */}
      <p className="mb-3 text-sm text-gray-500">
        Nahrávání se po {MAX_RECORDING_SECONDS} sekundách automaticky ukončí.
      </p>

      {/* Legend for the per-word colours */}
      <div className="mb-5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
        <span><span className="text-green-600 font-medium">slovo</span> = dobře</span>
        <span><span className="text-amber-600 font-medium">slovo</span> = nepřesně</span>
        <span><span className="text-red-600 font-medium">slovo</span> = špatně</span>
        <span><span className="text-gray-400 font-medium line-through">slovo</span> = vynecháno</span>
        <span><span className="text-orange-400 font-medium italic">slovo</span> = navíc</span>
      </div>

      {/* Questions list */}
      <div className="flex flex-col gap-4 mb-6">
        {questions.map((question) => {
          const isAnswered = userAnswers[question.id];
          const result = pronunciationResults[question.id];
          const isRecordingThis = recordingId === question.id;
          const isAssessingThis = assessingId === question.id;

          return (
            <div
              key={question.id}
              className={`p-4 rounded-lg transition-all ${
                isAnswered
                  ? 'bg-green-50 ring-2 ring-green-200'
                  : 'bg-gray-50'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Status icon */}
                <div className="mt-1">
                  {isAnswered ? (
                    <CheckCircle size={24} className="text-green-500" strokeWidth={2} />
                  ) : (
                    <Circle size={24} className="text-gray-400" strokeWidth={2} />
                  )}
                </div>

                {/* Question content */}
                <div className="flex-1">
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-gray-500 text-sm">Otázka:</span>
                      <TranslatableText text={question.question} textColor="#1f2937" topic={topic} />
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-gray-500 text-sm">Řekněte nahlas:</span>
                      <TranslatableText text={question.hint} textColor="#1f2937" topic={topic} />
                      <button
                        onClick={() => playAudio(question.id, question.hint)}
                        disabled={playingId !== null}
                        className={`px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm font-medium transition-all ${
                          playingId === question.id
                            ? 'bg-amber-500 text-white'
                            : playingId !== null
                              ? 'bg-cyan-400 text-white opacity-60 cursor-not-allowed'
                              : 'bg-cyan-500 hover:bg-cyan-600 text-white cursor-pointer'
                        }`}
                      >
                        <Volume2 size={16} />
                        {playingId === question.id ? 'Přehrává se...' : 'Poslechnout'}
                      </button>
                    </div>
                  </div>

                  {/* Pronunciation score */}
                  {result && (
                    <>
                      <div className="mb-2 flex items-center gap-3 flex-wrap text-sm">
                        <span className="text-gray-500">Přesnost výslovnosti:</span>
                        <span className={`text-lg font-bold ${scoreColor(result.accuracy)}`}>
                          {Math.round(result.accuracy)} / 100
                        </span>
                      </div>

                      {/* Per-word breakdown — hover a word to see its score */}
                      <PronunciationWords words={result.words} className="mb-3 text-base" />

                    </>
                  )}

                  {/* Action buttons */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      onClick={() => handleRecordClick(question)}
                      disabled={isAssessingThis || (recordingId !== null && !isRecordingThis)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                        isRecordingThis
                          ? 'bg-red-500 hover:bg-red-600 text-white'
                          : isAssessingThis
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-purple-500 hover:bg-purple-600 text-white'
                      }`}
                    >
                      {isAssessingThis ? (
                        <>
                          <Loader2 size={16} className="animate-spin" /> Vyhodnocuji...
                        </>
                      ) : isRecordingThis ? (
                        <>
                          <Square size={16} /> Zastavit a vyhodnotit
                        </>
                      ) : (
                        <>
                          <Mic size={16} /> {result ? 'Nahrát znovu' : 'Nahrát'}
                        </>
                      )}
                    </button>

                    {isAnswered && (
                      <span className="px-4 py-2 rounded-lg text-sm font-medium bg-green-100 text-green-700">
                        ✓ Hotovo
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {assessError && (
        <div className="mb-4 text-center text-sm text-red-600">{assessError}</div>
      )}

      {/* Submit button */}
      <div className="flex gap-3 justify-center">
        <button
          onClick={onSubmit}
          disabled={!allAnswered}
          className={`px-6 py-3 rounded-lg font-medium transition-all ${
            allAnswered
              ? 'bg-green-500 hover:bg-green-600 text-white cursor-pointer'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Odevzdat odpovědi a návrat do menu
        </button>
      </div>

      {!allAnswered && (
        <div className="mt-4 text-center text-sm text-gray-500">
          Nahrajte všechny věty před odevzdáním
        </div>
      )}
    </ExerciseCard>
  );
};

export default SpeakingPanel;
